import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Hook for media query
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, [matches, query]);
  return matches;
};

// Format date for labels and handle ranges
const formatSingleDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString; // Return original if invalid date
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatRangeForDisplay = (rangeString) => {
  if (!rangeString) return "";
  if (rangeString.includes(" to ")) {
    const parts = rangeString.split(" to ").map(part => part.trim());
    const formattedParts = parts.map(formatSingleDateForDisplay);
    return formattedParts.join(" to ");
  }
  return formatSingleDateForDisplay(rangeString);
};

const COLOR_PALETTE = [
  "#8856d8", "#3b82f6", "#10b981", "#f97316", "#ef4444",
  "#06b6d4", "#ec4899", "#facc15", "#6366f1", "#22c55e",
  "#6b7280", "#8b5cf6",
];

// Custom tooltip with scrollable content
const CustomTooltip = ({ active, payload, label, activeSubject = 'Default Subject', isMobile = false, indexToDateMap = {} }) => {
  if (active && payload && payload.length) {
    const sortedPayload = [...payload].sort((a, b) => {
      if (a.name.toLowerCase() === activeSubject.toLowerCase()) return -1;
      if (b.name.toLowerCase() === activeSubject.toLowerCase()) return 1;
      return 0;
    });

    // Custom Indian date format (DD/MM/YYYY)
    const formatIndianDate = (dateStr) => {
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr; // Fallback if invalid
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch {
        return dateStr; // Fallback to raw label
      }
    };
    const rawDateLabel = isMobile && indexToDateMap[label] ? indexToDateMap[label] : label;
    const displayLabel = formatRangeForDisplay(rawDateLabel).replace(/ to /g, ' to ');

    return (
      <div
        style={{
          width: '240px',
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          overflow: 'visible',
          zIndex: 1000,
          pointerEvents: 'auto',
        }}
      >
        <p
          style={{
            marginBottom: '8px',
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: '4px',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937',
          }}
        >
          {displayLabel}
        </p>
        <div
          style={{
            maxHeight: '180px',
            overflowY: 'scroll',
            paddingRight: '8px',
            minHeight: '50px',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            scrollbarColor: '#d1d5db #f3f4f6',
            pointerEvents: 'auto',
          }}
        >
          {sortedPayload.map((entry, index) => (
            <div
              key={`item-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '12px',
                padding: '6px 0',
                minHeight: '24px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: entry.stroke || '#8884d8',
                    marginRight: '8px',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    color: '#4b5563',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {entry.name}
                </span>
              </div>
              <span
                style={{
                  fontWeight: '700',
                  color: '#1f2937',
                  paddingLeft: '8px',
                  flexShrink: 0,
                }}
              >
                {Number(entry.value).toFixed(2) === '0.00' ? '-' : `${Number(entry.value).toFixed(2)}%`}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

// Custom tick for desktop X-Axis to handle date ranges
const CustomTick = ({ x, y, payload }) => {
  if (!payload || !payload.value) return null;
  const rawValue = String(payload.value);
  if (rawValue.includes(" to ")) {
    const parts = rawValue.split(" to ");
    const startDate = parts[0] ? formatSingleDateForDisplay(parts[0].trim()) : "";
    const endDate = parts[1] ? formatSingleDateForDisplay(parts[1].trim()) : "";
    return (
      <g transform={`translate(${x},${y})`}>
        <text dy={12} textAnchor="middle" fill="#6c757d" fontSize="12">
          {startDate}
        </text>
        <text dy={28} textAnchor="middle" fill="#6c757d" fontSize="12">
          to
        </text>
        <text dy={44} textAnchor="middle" fill="#6c757d" fontSize="12">
          {endDate}
        </text>
      </g>
    );
  }
  const singleDate = formatSingleDateForDisplay(rawValue.trim());
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={16} textAnchor="middle" fill="#6c757d" fontSize="12">
        {singleDate}
      </text>
    </g>
  );
};

const BehaviourScoreChart = ({
  chartData = [],
  subjects = [],
  activeSubject = "",
  analysisRange = null,
  chartTitle = "Behaviour Analysis",
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [highlightedSubject, setHighlightedSubject] = useState(null);
  const [legendTooltip, setLegendTooltip] = useState(null); // State for tooltip

  const subjectColorMap = useMemo(() => {
    const map = {};
    subjects.forEach((subject, index) => {
      map[subject] =
        subject.toLowerCase() === activeSubject.toLowerCase()
          ? COLOR_PALETTE[0]
          : COLOR_PALETTE[(index % (COLOR_PALETTE.length - 1)) + 1];
    });
    return map;
  }, [subjects, activeSubject]);

  const indexToDateMap = useMemo(() => {
    const map = {};
    chartData.forEach((point, idx) => {
      map[idx + 1] = point.name;
    });
    return map;
  }, [chartData]);

  const transformedData = useMemo(() => {
    if (!isMobile) return chartData;
    return chartData.map((point, idx) => ({ ...point, index: idx + 1 }));
  }, [isMobile, chartData]);

  const yAxisDomain = useMemo(() => {
    if (!subjects.length || !chartData.length) return [0, 10];
    let maxScore = 0;
    chartData.forEach((point) => {
      subjects.forEach((subject) => {
        const score = point[subject];
        if (typeof score === "number" && score > maxScore) maxScore = score;
      });
    });
    return [0, Math.ceil(maxScore) + 5];
  }, [chartData, subjects]);

  const tickCount = useMemo(() => {
    const maxVal = yAxisDomain[1];
    return maxVal <= 20 ? 5 : maxVal <= 50 ? 7 : 10;
  }, [yAxisDomain]);

  const chartMargin = useMemo(
    () => (isMobile ? { top: 5, right: 15, left: -15, bottom: 20 } : { top: 5, right: 30, left: 10, bottom: 20 }),
    [isMobile]
  );

  // Handle touch for mobile tooltip (hover-like behavior)
  let touchTimeout = null;
  const handleTouchStart = (index, date, event) => {
    if (!isMobile) return;
    console.log("Touch started on legend item:", { index, date }); // Debug log
    // Clear any existing timeout
    if (touchTimeout) clearTimeout(touchTimeout);
    // Delay to confirm touch-and-hold (500ms)
    touchTimeout = setTimeout(() => {
      const touch = event.touches[0];
      const rect = event.currentTarget.getBoundingClientRect();
      const x = touch.clientX;
      const y = touch.clientY - rect.height - 10; // Above the element
      setLegendTooltip({
        text: formatRangeForDisplay(date),
        x,
        y,
      });
      console.log("Tooltip set:", { text: formatRangeForDisplay(date), x, y }); // Debug log
    }, 500); // 500ms delay for hover-like behavior
  };

  const handleTouchMove = (event) => {
    if (!isMobile || !legendTooltip) return;
    // Hide tooltip if touch moves significantly
    const touch = event.touches[0];
    const dx = Math.abs(touch.clientX - legendTooltip.x);
    const dy = Math.abs(touch.clientY - legendTooltip.y);
    if (dx > 10 || dy > 10) {
      if (touchTimeout) clearTimeout(touchTimeout);
      setLegendTooltip(null);
      console.log("Tooltip cleared due to touch move"); // Debug log
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    if (touchTimeout) clearTimeout(touchTimeout);
    setLegendTooltip(null);
    console.log("Tooltip cleared due to touch end"); // Debug log
  };

  // Fallback for testing in DevTools
  const handleMouseEnter = (index, date, event) => {
    if (!isMobile) return;
    console.log("Mouse enter on legend item:", { index, date }); // Debug log
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX;
    const y = rect.top - 10; // Above the element
    setLegendTooltip({
      text: formatRangeForDisplay(date),
      x,
      y,
    });
    console.log("Tooltip set (mouse):", { text: formatRangeForDisplay(date), x, y }); // Debug log
  };

  const handleMouseLeave = () => {
    if (!isMobile) return;
    setLegendTooltip(null);
    console.log("Tooltip cleared due to mouse leave"); // Debug log
  };

  if (!chartData.length || !subjects.length) {
    return (
      <div className="flex items-center justify-center w-full h-full text-gray-400">
        No behaviour data available.
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col min-h-0">
      {/* Header */}
      <div className={`w-full px-2 py-3 flex ${isMobile ? "flex-col items-start gap-3" : "flex-row justify-between items-center"}`}>
  <div className={isMobile ? "flex flex-col items-start gap-2" : ""}>
    <h3
      className="font-bold flex-shrink-0"
      style={{ fontSize: isMobile ? "1rem" : "1.3rem", color: "#111827" }}
    >
      {chartTitle}
    </h3>
    {isMobile && analysisRange && (
      <div className="flex items-center text-sm" style={{ color: "#111827" }}>
        <span>{formatSingleDateForDisplay(analysisRange.start)}</span>
        <span className="mx-2">to</span>
        <span>{formatSingleDateForDisplay(analysisRange.end)}</span>
      </div>
    )}
  </div>
  <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 ${isMobile ? "justify-start" : "justify-center"}`}>
    {subjects.map((subject) => {
      const color = subjectColorMap[subject] || "#ccc";
      const faded = highlightedSubject && highlightedSubject !== subject;
      return (
        <button
          key={subject}
          type="button"
          className={`flex items-center space-x-2 text-xs sm:text-sm focus:outline-none transition-colors ${
            faded ? "text-gray-400" : "text-gray-700"
          }`}
        >
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
          <span className="truncate max-w-[8rem]">{subject}</span>
        </button>
      );
    })}
  </div>
  {!isMobile && analysisRange && (
    <div className="flex items-center mb-1">
      <span>{formatSingleDateForDisplay(analysisRange.start)}</span>
      <span className="mx-2"> to </span>
      <span>{formatSingleDateForDisplay(analysisRange.end)}</span>
    </div>
  )}
</div>
      {/* Chart */}
      <div className="w-full flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={transformedData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey={isMobile ? "index" : "name"}
              tick={!isMobile ? <CustomTick /> : undefined}
              tickFormatter={isMobile ? (value) => value : undefined}
              interval={isMobile ? "equidistantPreserveStart" : "preserveStartEnd"}
              height={isMobile ? 40 : 70}
              label={{
                value: isMobile ? "Intervals" : "Time Intervals",
                position: "insideBottom",
                offset: isMobile ? -5 : -10,
                style: { fill: "black", fontSize: isMobile ? 11 : 14 },
              }}
            />
            <YAxis
              domain={yAxisDomain}
              tick={{ fill: "#6c757d", fontSize: isMobile ? 10 : 13 }}
              tickCount={tickCount}
              interval={0}
              allowDataOverflow
              tickFormatter={(tick) => `${tick}%`}
            />
            <Tooltip
              content={<CustomTooltip activeSubject={activeSubject} isMobile={isMobile} indexToDateMap={indexToDateMap} />}
              cursor={{
                stroke: subjectColorMap[activeSubject] || COLOR_PALETTE[0],
                strokeWidth: 1.5,
                strokeDasharray: "3 3",
              }}
            />
            {subjects.map((subject) => {
              const isHighlighted = highlightedSubject === subject;
              const isPrimary = subject.toLowerCase() === activeSubject.toLowerCase();
              const isActive = isHighlighted || (highlightedSubject === null && isPrimary);
              const color = subjectColorMap[subject] || "#ccc";

              return (
                <Line
                  key={subject}
                  type="monotone"
                  dataKey={subject}
                  stroke={color}
                  strokeWidth={isActive ? 3.5 : 1.5}
                  strokeOpacity={highlightedSubject ? (isActive ? 1 : 0.25) : 1}
                  dot={false}
                  activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
                  connectNulls
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Index → Date mapping for mobile */}
      {isMobile && (
        <div className="mt-2 px-2 relative">
          <h4 className="text-sm font-semibold text-gray-600 mb-2">Date Legend</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-500">
            {Object.entries(indexToDateMap).map(([index, date]) => (
              <div
                key={index}
                className="flex items-center truncate cursor-pointer"
                onTouchStart={(e) => handleTouchStart(index, date, e)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseEnter={(e) => handleMouseEnter(index, date, e)} // Fallback for DevTools
                onMouseLeave={handleMouseLeave} // Fallback for DevTools
              >
                <span className="font-semibold w-6 flex-shrink-0">{index}:</span>
                <span className="truncate">{formatRangeForDisplay(date)}</span>
              </div>
            ))}
          </div>
          {legendTooltip && (
            <div
              className="fixed bg-black border border-gray-200 rounded-xl p-3 shadow-lg text-sm  text-color-white"
              style={{
                left: `${legendTooltip.x}px`,
                top: `${legendTooltip.y}px`,
                transform: "translate(-50%, -100%)",
                zIndex: 10000, // Increased zIndex
                whiteSpace: "nowrap",
                color: "white",}}
            >
              {legendTooltip.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(BehaviourScoreChart);